// src/pages/Deposit.tsx
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/store";
import { deposit, getTransactions } from "../redux/slices/transactionSlice";
import { GlobalStyles } from "../styles/globalStyles";
import {
  ArrowDownRight,
  CheckCircle,
  AlertCircle,
  Wallet as WalletIcon,
  RefreshCw,
  History,
  TrendingUp,
  Calculator,
  Sparkles,
} from "lucide-react";
import toast from "react-hot-toast";
import { formatFiat, formatCrypto } from "../utils/currency";
import { transactionService } from "../services/api";

interface CryptoPrice {
  [key: string]: {
    price: number;
    network: string;
    crypto_id: string;
  };
}

const INITIAL_CURRENCIES = {
  BTC: { price: 0, network: "Bitcoin", crypto_id: "bitcoin" },
  ETH: { price: 0, network: "Ethereum", crypto_id: "ethereum" },
  USDT: { price: 0, network: "Ethereum", crypto_id: "tether" },
  BNB: { price: 0, network: "Binance Smart Chain", crypto_id: "binancecoin" },
  SOL: { price: 0, network: "Solana", crypto_id: "solana" },
};

interface CalculatedAmount {
  crypto_amount: number;
  currency: string;
  crypto_price: number;
  usd_amount: number;
  network: string;
  meets_minimum: boolean;
  minimum_required: number;
}

const Deposit: React.FC = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  const { transactions } = useSelector(
    (state: RootState) => state.transactions,
  );
  const [loading, setLoading] = useState(false);
  const [fetchingPrices, setFetchingPrices] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [formData, setFormData] = useState({
    crypto_amount: "",
    currency: "BTC",
  });
  const [cryptoPrices, setCryptoPrices] = useState<CryptoPrice>(() => {
    const savedPrices = localStorage.getItem("obelisk_crypto_prices");
    if (savedPrices) {
      try {
        const parsed = JSON.parse(savedPrices);
        return { ...INITIAL_CURRENCIES, ...parsed };
      } catch (e) {
        return INITIAL_CURRENCIES;
      }
    }
    return INITIAL_CURRENCIES;
  });
  const [lastUpdate, setLastUpdate] = useState<string>(() => {
    const savedUpdate = localStorage.getItem("obelisk_prices_last_update");
    return savedUpdate || "";
  });
  const [calculatedAmount, setCalculatedAmount] =
    useState<CalculatedAmount | null>(null);

  useEffect(() => {
    fetchCryptoPrices();
    dispatch(getTransactions({ type: "deposit" }) as any);

    const interval = setInterval(fetchCryptoPrices, 60000);
    return () => clearInterval(interval);
  }, [dispatch]);

  const savePricesToStorage = (prices: CryptoPrice, timestamp: number) => {
    try {
      localStorage.setItem("obelisk_crypto_prices", JSON.stringify(prices));
      localStorage.setItem(
        "obelisk_prices_last_update_timestamp",
        timestamp.toString(),
      );
      const updateTime = new Date(timestamp * 1000).toLocaleTimeString();
      setLastUpdate(updateTime);
      localStorage.setItem("obelisk_prices_last_update", updateTime);
    } catch (error) {
      console.error("Error saving prices to storage:", error);
    }
  };

  const fetchCryptoPrices = async () => {
    setFetchingPrices(true);
    try {
      const response = await transactionService.getCryptoPrices();

      if (response.success && response.prices) {
        const mergedPrices = { ...cryptoPrices };
        let hasNewData = false;

        Object.entries(response.prices).forEach(([currency, data]) => {
          const priceData = data as any;
          if (
            priceData &&
            typeof priceData === "object" &&
            typeof priceData.price === "number" &&
            priceData.price > 0
          ) {
            mergedPrices[currency] = priceData;
            hasNewData = true;
          }
        });

        if (hasNewData) {
          setCryptoPrices(mergedPrices);
          savePricesToStorage(
            mergedPrices,
            response.timestamp || Math.floor(Date.now() / 1000),
          );
          toast.success("Prices updated successfully");
        }
      } else {
        toast.success("Using cached prices");
        const savedTimestamp = localStorage.getItem(
          "obelisk_prices_last_update_timestamp",
        );
        if (savedTimestamp) {
          const timestamp = parseInt(savedTimestamp);
          const updateTime = new Date(timestamp * 1000).toLocaleTimeString();
          setLastUpdate(updateTime);
        }
      }
    } catch (error: any) {
      console.error("Price fetch error:", error);
      const savedTimestamp = localStorage.getItem(
        "obelisk_prices_last_update_timestamp",
      );
      if (savedTimestamp) {
        const timestamp = parseInt(savedTimestamp);
        const updateTime = new Date(timestamp * 1000).toLocaleTimeString();
        setLastUpdate(updateTime);
        toast.error("Network error - Using cached prices");
      } else {
        toast.error("Failed to fetch prices - Please try again");
      }
    } finally {
      setFetchingPrices(false);
    }
  };

  const handleCalculate = async () => {
    if (!formData.crypto_amount || parseFloat(formData.crypto_amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    const currentPrice = cryptoPrices[formData.currency]?.price;
    if (!currentPrice || currentPrice === 0) {
      toast.error("Please wait for prices to load");
      return;
    }

    const cryptoAmount = parseFloat(formData.crypto_amount);
    const usdAmount = cryptoAmount * currentPrice;
    const minDepositUsd = 10;
    const meetsMinimum = usdAmount >= minDepositUsd;

    const network =
      cryptoPrices[formData.currency]?.network ||
      INITIAL_CURRENCIES[formData.currency as keyof typeof INITIAL_CURRENCIES]
        ?.network ||
      "Unknown Network";

    const localCalculation: CalculatedAmount = {
      crypto_amount: cryptoAmount,
      currency: formData.currency,
      crypto_price: currentPrice,
      usd_amount: usdAmount,
      network: network,
      meets_minimum: meetsMinimum,
      minimum_required: minDepositUsd,
    };

    if (!meetsMinimum) {
      toast.error(
        `Minimum deposit is ${formatFiat(minDepositUsd)}. Your amount is ${formatFiat(
          usdAmount,
        )}.`,
      );
      setCalculatedAmount(localCalculation);
      return;
    }

    setCalculating(true);
    try {
      const response = await transactionService.calculateDepositAmount({
        crypto_amount: cryptoAmount,
        currency: formData.currency,
        current_price: currentPrice,
      });

      if (response.success) {
        setCalculatedAmount(response);
      } else {
        setCalculatedAmount(localCalculation);
        if (response.error) {
          toast.error(response.error);
        }
      }
    } catch (error: any) {
      setCalculatedAmount(localCalculation);
      console.error("Backend calculation failed, using local:", error);
    } finally {
      setCalculating(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.crypto_amount || parseFloat(formData.crypto_amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    if (!calculatedAmount) {
      toast.error("Please calculate the amount first");
      return;
    }

    if (!calculatedAmount.meets_minimum) {
      toast.error(
        `Minimum deposit is ${formatFiat(calculatedAmount.minimum_required)}`,
      );
      return;
    }

    setLoading(true);
    try {
      await dispatch(
        deposit({
          crypto_amount: parseFloat(formData.crypto_amount),
          currency: formData.currency,
          current_price: calculatedAmount.crypto_price,
        }) as any,
      );

      toast.success("Deposit submitted successfully!");
      setFormData({ crypto_amount: "", currency: "BTC" });
      setCalculatedAmount(null);
      dispatch(getTransactions({ type: "deposit" }) as any);
    } catch (error: any) {
      toast.error(error.response?.data?.error || "Failed to submit deposit");
    } finally {
      setLoading(false);
    }
  };

  const recentDeposits = transactions
    .filter((t) => t.type === "deposit")
    .slice(0, 3);

  const getCurrentPrice = (currency: string) => {
    return cryptoPrices[currency]?.price || 0;
  };

  const formatPriceDisplay = (price: number, currency: string) => {
    if (price === 0) {
      return fetchingPrices ? "Loading..." : "—";
    }

    if (price > 1000) {
      return `$${price.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    } else if (price > 1) {
      return `$${price.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
      })}`;
    } else {
      return `$${price.toLocaleString(undefined, {
        minimumFractionDigits: 6,
        maximumFractionDigits: 6,
      })}`;
    }
  };

  const getLastUpdateDisplay = () => {
    if (fetchingPrices) return "Updating...";
    if (lastUpdate) return `Last updated: ${lastUpdate}`;
    const savedUpdate = localStorage.getItem("obelisk_prices_last_update");
    if (savedUpdate) {
      setLastUpdate(savedUpdate);
      return `Last updated: ${savedUpdate}`;
    }
    return "Never updated";
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-gray-200 dark:from-gray-900 dark:via-gray-800 dark:to-gray-950">
      <div className="p-4 lg:p-5">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h1 className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent">
                  Make Deposit
                </h1>
                <Sparkles className="h-4 w-4 text-primary/60" />
              </div>
              <p className="text-gray-500 dark:text-gray-400">
                Deposit cryptocurrency using live market prices
              </p>
            </div>
            <button
              onClick={fetchCryptoPrices}
              disabled={fetchingPrices}
              className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonMedium} disabled:opacity-50 group/refresh`}
            >
              {fetchingPrices ? (
                <div className="flex items-center">
                  <div
                    className={`${GlobalStyles.spinner} w-4 h-4 mr-2 border-2`}
                  ></div>
                  Updating...
                </div>
              ) : (
                <div className="flex items-center">
                  <RefreshCw className="h-4 w-4 mr-2 transition-transform group-hover/refresh:rotate-180" />
                  Refresh Prices
                </div>
              )}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left Column - Deposit Form */}
          <div className="lg:col-span-2 space-y-4">
            {/* Price Ticker */}
            <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-xl border border-gray-200/30 dark:border-gray-700/30 p-5 shadow-lg">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent">
                  Crypto Prices
                </h3>
                <div className="flex items-center gap-2 text-sm">
                  <TrendingUp
                    className={`h-4 w-4 ${fetchingPrices ? "text-yellow-500" : "text-green-500"}`}
                  />
                  <span
                    className={`${fetchingPrices ? "text-yellow-500" : "text-gray-500 dark:text-gray-400"}`}
                  >
                    {getLastUpdateDisplay()}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                {Object.keys(INITIAL_CURRENCIES).map((currency) => {
                  const priceData = cryptoPrices[currency];
                  const price = priceData?.price || 0;
                  const network =
                    priceData?.network ||
                    INITIAL_CURRENCIES[
                      currency as keyof typeof INITIAL_CURRENCIES
                    ]?.network ||
                    "Unknown";

                  return (
                    <div
                      key={currency}
                      className={`p-2 rounded-lg text-center transition-all duration-200 group/ticker ${
                        fetchingPrices
                          ? "bg-gray-100/50 dark:bg-gray-800/30 animate-pulse"
                          : price === 0
                            ? "bg-gray-100/50 dark:bg-gray-800/30"
                            : "bg-gray-50/50 dark:bg-gray-800/50 hover:bg-primary/5"
                      }`}
                    >
                      <div className="font-semibold text-gray-900 dark:text-white mb-1 group-hover/ticker:text-primary transition-colors">
                        {currency}
                      </div>
                      <div
                        className={`text-xs font-medium ${
                          fetchingPrices
                            ? "text-gray-400 dark:text-gray-500"
                            : price === 0
                              ? "text-gray-500 dark:text-gray-400"
                              : "text-green-600 dark:text-green-400"
                        }`}
                      >
                        {formatPriceDisplay(price, currency)}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">
                        {network}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Deposit Form */}
            <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-xl border border-gray-200/30 dark:border-gray-700/30 p-5 shadow-lg">
              <h3 className="text-lg font-semibold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent mb-6">
                Deposit Details
              </h3>

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Crypto Amount */}
                <div className="group">
                  <label
                    className={`${GlobalStyles.inputLabel} mb-2 flex items-center gap-1`}
                  >
                    <span>Cryptocurrency Amount</span>
                    <Sparkles className="h-3 w-3 text-primary/60" />
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.00000001"
                      value={formData.crypto_amount}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          crypto_amount: e.target.value,
                        })
                      }
                      className={`${GlobalStyles.input} pl-10 focus:pl-11 transition-all duration-300`}
                      placeholder="0.00000000"
                      required
                      min="0.00000001"
                    />
                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                      <WalletIcon className="h-4 w-4 text-primary group-focus-within:text-primary/80 transition-colors" />
                    </div>
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <select
                        value={formData.currency}
                        onChange={(e) =>
                          setFormData({ ...formData, currency: e.target.value })
                        }
                        className="bg-transparent border-none focus:outline-none text-gray-700 dark:text-gray-300"
                        disabled={fetchingPrices}
                      >
                        {Object.keys(INITIAL_CURRENCIES).map((currency) => (
                          <option key={currency} value={currency}>
                            {currency}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="flex justify-between mt-1">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Current price:{" "}
                      {formatPriceDisplay(
                        getCurrentPrice(formData.currency),
                        formData.currency,
                      )}
                    </p>
                    {getCurrentPrice(formData.currency) > 0 &&
                      formData.crypto_amount && (
                        <p className="text-sm text-green-600 dark:text-green-400">
                          ≈{" "}
                          {formatFiat(
                            parseFloat(formData.crypto_amount) *
                              getCurrentPrice(formData.currency),
                          )}
                        </p>
                      )}
                  </div>
                </div>

                {/* Calculate Button */}
                <div>
                  <button
                    type="button"
                    onClick={handleCalculate}
                    disabled={
                      calculating ||
                      !formData.crypto_amount ||
                      fetchingPrices ||
                      getCurrentPrice(formData.currency) === 0
                    }
                    className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonMedium} w-full group/calc disabled:opacity-50`}
                  >
                    {calculating ? (
                      <div className="flex items-center justify-center">
                        <div
                          className={`${GlobalStyles.spinner} w-4 h-4 mr-2 border-2`}
                        ></div>
                        Calculating...
                      </div>
                    ) : fetchingPrices ? (
                      <div className="flex items-center justify-center">
                        <div
                          className={`${GlobalStyles.spinner} w-4 h-4 mr-2 border-2`}
                        ></div>
                        Loading Prices...
                      </div>
                    ) : getCurrentPrice(formData.currency) === 0 ? (
                      <div className="flex items-center justify-center">
                        <AlertCircle className="h-4 w-4 mr-2 text-yellow-500" />
                        No Price Available
                      </div>
                    ) : (
                      <div className="flex items-center justify-center">
                        <Calculator className="h-4 w-4 mr-2 transition-transform group-hover/calc:scale-110" />
                        Calculate Amount
                      </div>
                    )}
                  </button>
                </div>

                {/* Calculated Amount Display */}
                {calculatedAmount && (
                  <div
                    className={`p-4 border rounded-lg backdrop-blur-sm ${
                      calculatedAmount.meets_minimum
                        ? "bg-green-50/50 dark:bg-green-900/20 border-green-200 dark:border-green-800"
                        : "bg-yellow-50/50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h4
                        className={`font-medium ${calculatedAmount.meets_minimum ? "text-green-800 dark:text-green-300" : "text-yellow-800 dark:text-yellow-300"}`}
                      >
                        Deposit Summary
                      </h4>
                      {calculatedAmount.meets_minimum ? (
                        <CheckCircle className="h-5 w-5 text-green-500" />
                      ) : (
                        <AlertCircle className="h-5 w-5 text-yellow-500" />
                      )}
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          Cryptocurrency:
                        </span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {formatCrypto(
                            calculatedAmount.crypto_amount,
                            calculatedAmount.currency,
                          )}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          Exchange Rate:
                        </span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          1 {calculatedAmount.currency} = $
                          {calculatedAmount.crypto_price.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          Network:
                        </span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {calculatedAmount.network}
                        </span>
                      </div>
                      <div className="pt-2 border-t border-current">
                        <div className="flex justify-between">
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            Total Deposit:
                          </span>
                          <span
                            className={`text-lg font-bold ${calculatedAmount.meets_minimum ? "text-green-600 dark:text-green-400" : "text-yellow-600 dark:text-yellow-400"}`}
                          >
                            {formatFiat(calculatedAmount.usd_amount)}
                          </span>
                        </div>
                      </div>
                      {!calculatedAmount.meets_minimum && (
                        <div className="mt-2 p-2 bg-red-50/50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded">
                          <p className="text-sm text-red-600 dark:text-red-400 text-center">
                            Minimum deposit:{" "}
                            {formatFiat(calculatedAmount.minimum_required)}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <div>
                  <button
                    type="submit"
                    disabled={
                      loading ||
                      !calculatedAmount ||
                      fetchingPrices ||
                      !calculatedAmount?.meets_minimum
                    }
                    className={`${GlobalStyles.button} ${GlobalStyles.buttonPrimary} ${GlobalStyles.buttonMedium} w-full group/submit disabled:opacity-50`}
                  >
                    {loading ? (
                      <div className="flex items-center justify-center">
                        <div
                          className={`${GlobalStyles.spinner} w-4 h-4 mr-2 border-2`}
                        ></div>
                        Processing...
                      </div>
                    ) : fetchingPrices ? (
                      <div className="flex items-center justify-center">
                        <div
                          className={`${GlobalStyles.spinner} w-4 h-4 mr-2 border-2`}
                        ></div>
                        Loading...
                      </div>
                    ) : (
                      <div className="flex items-center justify-center">
                        <ArrowDownRight className="h-4 w-4 mr-2 transition-transform group-hover/submit:-translate-y-0.5" />
                        <span>Confirm Deposit</span>
                      </div>
                    )}
                  </button>
                </div>
              </form>
            </div>

            {/* Recent Deposits */}
            <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-xl border border-gray-200/30 dark:border-gray-700/30 p-5 shadow-lg">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent">
                  Recent Deposits
                </h3>
                <button
                  onClick={() => (window.location.href = "/transactions")}
                  className={`${GlobalStyles.button} ${GlobalStyles.buttonGhost} ${GlobalStyles.buttonSmall} group/view`}
                >
                  <History className="h-4 w-4 mr-2 transition-transform group-hover/view:translate-x-0.5" />
                  View All
                </button>
              </div>
              <div className="space-y-2">
                {recentDeposits.length > 0 ? (
                  recentDeposits.map((transaction) => (
                    <div
                      key={transaction.id}
                      className="flex items-center justify-between p-3 bg-gray-50/50 dark:bg-gray-800/50 rounded-lg hover:bg-gray-100/50 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {formatFiat(transaction.amount)}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {new Date(
                            transaction.created_at,
                          ).toLocaleDateString()}{" "}
                          • {transaction.tx_hash?.substring(0, 12)}...
                        </p>
                      </div>
                      <span
                        className={`${GlobalStyles.badge} ${
                          transaction.status === "confirmed"
                            ? GlobalStyles.badgeSuccess
                            : transaction.status === "pending"
                              ? GlobalStyles.badgeWarning
                              : GlobalStyles.badgeError
                        } px-2 py-0.5`}
                      >
                        {transaction.status}
                      </span>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-4">
                    <p className="text-gray-500 dark:text-gray-400">
                      No deposits yet
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Info Panels */}
          <div className="space-y-4">
            {/* Important Info */}
            <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-xl border border-gray-200/30 dark:border-gray-700/30 p-5 shadow-lg">
              <h3 className="text-lg font-semibold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent mb-4">
                Important Information
              </h3>
              <div className="space-y-2">
                <div className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Minimum deposit: $10 USD equivalent
                  </p>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Uses live prices
                  </p>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    All deposits require confirmation
                  </p>
                </div>
              </div>
            </div>

            {/* Current Balance */}
            <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-xl border border-gray-200/30 dark:border-gray-700/30 p-5 shadow-lg">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  Current Balance
                </h3>
                <WalletIcon className="h-5 w-5 text-primary" />
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent">
                  {formatFiat(user?.balance || 0)}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Available for withdrawal
                </p>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default Deposit;
